/**
 * V66 Phase 2 — HR 휴직 관리 섹션 (직원 상세 모달의 한 탭)
 *
 * 기능:
 *  - 직원의 휴직 이력 표시
 *  - 휴직 등록 폼 (HR 직접 등록)
 *  - 시작 전(approved) 휴직 → 취소 가능
 *  - 진행 중(inProgress) 또는 시작 전(approved) 휴직 → 복직 처리 가능
 *
 * BE 권한 검증은 callable 단에서 수행. FE 는 표시·입력만.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import {
  LeaveOfAbsenceItem,
  LeaveOfAbsenceType,
  LOA_TYPE_LABELS,
  LOA_TYPE_MAX_LABELS,
  LOA_STATUS_LABELS,
  useLeaveOfAbsenceList,
  createLeaveOfAbsence,
  executeReturnFromLeave,
  cancelLeaveOfAbsence,
  calcMaxExpectedReturnDate,
} from '../../../../shared/hooks/useLeaveOfAbsence';

interface Props {
  employeeId: string;
  employeeName: string;
  /** 조회 전용 모드 (액션 버튼 숨김) */
  readOnly?: boolean;
  /** 모달 외부에서 성공 콜백 (직원관리 리스트 갱신 등) */
  onChanged?: () => void;
}

const TYPE_OPTIONS: { value: LeaveOfAbsenceType; label: string; max: string }[] = [
  { value: 'parental', label: LOA_TYPE_LABELS.parental, max: LOA_TYPE_MAX_LABELS.parental },
  { value: 'familyCare', label: LOA_TYPE_LABELS.familyCare, max: LOA_TYPE_MAX_LABELS.familyCare },
  { value: 'general', label: LOA_TYPE_LABELS.general, max: LOA_TYPE_MAX_LABELS.general },
  { value: 'medical', label: LOA_TYPE_LABELS.medical, max: LOA_TYPE_MAX_LABELS.medical },
];

function todayYmd(): string {
  const d = new Date();
  const p = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function statusColor(status: LeaveOfAbsenceItem['status']): { bg: string; fg: string } {
  switch (status) {
    case 'approved': return { bg: '#fef3c7', fg: '#92400e' };
    case 'inProgress': return { bg: '#dbeafe', fg: '#1e40af' };
    case 'returned': return { bg: '#dcfce7', fg: '#166534' };
    case 'cancelled': return { bg: '#f3f4f6', fg: '#6b7280' };
  }
}

export function LeaveOfAbsenceSection({ employeeId, employeeName, readOnly = false, onChanged }: Props) {
  const { items, loading, error, refetch } = useLeaveOfAbsenceList(employeeId);

  // 등록 폼 상태
  const [type, setType] = useState<LeaveOfAbsenceType>('parental');
  const [startDate, setStartDate] = useState<string>(todayYmd());
  const [expectedReturnDate, setExpectedReturnDate] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // 복직/취소 다이얼로그 상태
  const [returnTarget, setReturnTarget] = useState<LeaveOfAbsenceItem | null>(null);
  const [returnDate, setReturnDate] = useState<string>(todayYmd());
  const [cancelTarget, setCancelTarget] = useState<LeaveOfAbsenceItem | null>(null);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [actionBusy, setActionBusy] = useState<boolean>(false);

  const maxExpectedDate = useMemo(() => calcMaxExpectedReturnDate(type, startDate), [type, startDate]);

  // 시작일 변경 시 기본 expectedReturnDate 도 최대값으로 자동 안내(미입력일 때만)
  useEffect(() => {
    if (!expectedReturnDate && maxExpectedDate) {
      setExpectedReturnDate(maxExpectedDate);
    }
    // 입력된 종료일이 최대값을 초과하면 자동 보정
    if (expectedReturnDate && maxExpectedDate && expectedReturnDate > maxExpectedDate) {
      setExpectedReturnDate(maxExpectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, startDate]);

  const canSubmit =
    !!startDate && !!expectedReturnDate && startDate <= expectedReturnDate &&
    (!maxExpectedDate || expectedReturnDate <= maxExpectedDate) && !submitting && !readOnly;

  const handleCreate = async () => {
    setFeedback(null);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await createLeaveOfAbsence({
        employeeId,
        type,
        startDate,
        expectedReturnDate,
        reason: reason.trim() || undefined,
      });
      setFeedback({ type: 'success', msg: `${employeeName}님 휴직 등록 완료.` });
      setReason('');
      // 폼 시작값 초기화 (다음 입력 위해)
      setStartDate(todayYmd());
      setExpectedReturnDate('');
      await refetch();
      onChanged?.();
    } catch (err: any) {
      console.error('[V66P2] createLeaveOfAbsence 실패:', err);
      setFeedback({ type: 'error', msg: err?.message || '휴직 등록 실패' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturn = async () => {
    if (!returnTarget) return;
    setActionBusy(true);
    setFeedback(null);
    try {
      await executeReturnFromLeave({ leaveOfAbsenceId: returnTarget.id, actualReturnDate: returnDate });
      setFeedback({ type: 'success', msg: '복직 처리 완료.' });
      setReturnTarget(null);
      await refetch();
      onChanged?.();
    } catch (err: any) {
      setFeedback({ type: 'error', msg: err?.message || '복직 처리 실패' });
    } finally {
      setActionBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setActionBusy(true);
    setFeedback(null);
    try {
      await cancelLeaveOfAbsence({ leaveOfAbsenceId: cancelTarget.id, reason: cancelReason.trim() || undefined });
      setFeedback({ type: 'success', msg: '휴직(예정) 취소 완료.' });
      setCancelTarget(null);
      setCancelReason('');
      await refetch();
      onChanged?.();
    } catch (err: any) {
      setFeedback({ type: 'error', msg: err?.message || '휴직 취소 실패' });
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div>
      {/* 피드백 */}
      {feedback && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
            background: feedback.type === 'success' ? '#f0fdf4' : '#fef2f2',
            border: feedback.type === 'success' ? '1px solid #bbf7d0' : '1px solid #fecaca',
            color: feedback.type === 'success' ? '#166534' : '#dc2626',
          }}
        >
          {feedback.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {feedback.msg}
        </div>
      )}

      {/* 등록 폼 */}
      {!readOnly && (
        <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid var(--color-gray-200)', borderRadius: '8px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>휴직 등록</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            <div>
              <label style={fieldLabel}>휴직 종류</label>
              <select value={type} onChange={(e) => setType(e.target.value as LeaveOfAbsenceType)} style={inputStyle}>
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label} (최대 {opt.max})</option>
                ))}
              </select>
            </div>
            <div>
              <label style={fieldLabel}>시작일</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={fieldLabel}>종료일</label>
              <input
                type="date"
                value={expectedReturnDate}
                onChange={(e) => setExpectedReturnDate(e.target.value)}
                min={startDate}
                max={maxExpectedDate || undefined}
                style={inputStyle}
              />
              <div style={{ fontSize: '11px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                {maxExpectedDate ? `최대 가능: ${maxExpectedDate}` : ' '}
              </div>
            </div>
            <div>
              <label style={fieldLabel}>사유 (옵션)</label>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="HR 메모" style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
            <button
              onClick={handleCreate}
              disabled={!canSubmit}
              style={{
                padding: '8px 16px', borderRadius: '6px', border: 'none',
                background: canSubmit ? 'var(--color-primary)' : 'var(--color-gray-300)',
                color: 'white', fontSize: '13px', fontWeight: 500,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting ? '등록 중...' : '휴직 등록'}
            </button>
          </div>
        </div>
      )}

      {/* 이력 */}
      <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>휴직 이력</div>
      {loading ? (
        <div style={{ padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-gray-500)' }}>
          <Loader2 size={16} className="animate-spin" /> <span style={{ marginLeft: 6 }}>조회 중...</span>
        </div>
      ) : error ? (
        <div style={{ padding: '16px', color: 'var(--color-red-600)', fontSize: '13px' }}>{error}</div>
      ) : items.length === 0 ? (
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-gray-500)', fontSize: '13px' }}>
          등록된 휴직 이력이 없습니다.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {items.map((it) => {
            const c = statusColor(it.status);
            const canCancelHere = !readOnly && it.status === 'approved';
            const canReturnHere = !readOnly && (it.status === 'approved' || it.status === 'inProgress');
            return (
              <div key={it.id} style={{ padding: '12px', border: '1px solid var(--color-gray-200)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 600 }}>{it.typeName || LOA_TYPE_LABELS[it.type]}</span>
                      <span style={{
                        padding: '2px 8px', borderRadius: '999px', fontSize: '11px',
                        background: c.bg, color: c.fg, fontWeight: 500,
                      }}>
                        {LOA_STATUS_LABELS[it.status]}
                      </span>
                      {it.isCurrent && (
                        <span style={{
                          padding: '2px 8px', borderRadius: '999px', fontSize: '11px',
                          background: '#fee2e2', color: '#dc2626', fontWeight: 500,
                        }}>현재 휴직중</span>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--color-gray-700)' }}>
                      {it.startDate} ~ {it.actualReturnDate || it.expectedReturnDate || '?'}
                      {it.actualReturnDate && it.expectedReturnDate && it.actualReturnDate !== it.expectedReturnDate ? (
                        <span style={{ marginLeft: 8, color: 'var(--color-gray-500)', fontSize: '12px' }}>
                          (종료 {it.expectedReturnDate})
                        </span>
                      ) : null}
                    </div>
                    {it.reason && (
                      <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginTop: '4px' }}>사유: {it.reason}</div>
                    )}
                    {it.cancelReason && (
                      <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginTop: '4px' }}>취소사유: {it.cancelReason}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    {canReturnHere && (
                      <button
                        onClick={() => { setReturnTarget(it); setReturnDate(todayYmd()); }}
                        style={btnStyle('var(--color-primary)')}
                      >
                        복직 처리
                      </button>
                    )}
                    {canCancelHere && (
                      <button
                        onClick={() => { setCancelTarget(it); setCancelReason(''); }}
                        style={btnStyle('var(--color-gray-600)')}
                      >
                        취소
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 복직 처리 다이얼로그 */}
      {returnTarget && (
        <div onClick={() => !actionBusy && setReturnTarget(null)} style={overlayStyle}>
          <div onClick={(e) => e.stopPropagation()} style={dialogStyle}>
            <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>복직 처리</div>
            <div style={{ fontSize: '13px', color: 'var(--color-gray-700)', marginBottom: '12px' }}>
              {returnTarget.typeName} ({returnTarget.startDate} ~ {returnTarget.expectedReturnDate || '?'})
            </div>
            <label style={fieldLabel}>실제 복귀일</label>
            <input
              type="date"
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
              min={returnTarget.startDate}
              style={inputStyle}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setReturnTarget(null)} disabled={actionBusy} style={btnStyle('var(--color-gray-500)')}>취소</button>
              <button onClick={handleReturn} disabled={actionBusy || !returnDate} style={btnStyle('var(--color-primary)')}>
                {actionBusy ? '처리 중...' : '복직 처리'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 휴직 취소 다이얼로그 */}
      {cancelTarget && (
        <div onClick={() => !actionBusy && setCancelTarget(null)} style={overlayStyle}>
          <div onClick={(e) => e.stopPropagation()} style={dialogStyle}>
            <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>휴직(예정) 취소</div>
            <div style={{ fontSize: '13px', color: 'var(--color-gray-700)', marginBottom: '12px' }}>
              {cancelTarget.typeName} ({cancelTarget.startDate} ~ {cancelTarget.expectedReturnDate || '?'})
            </div>
            <label style={fieldLabel}>사유 (옵션)</label>
            <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="취소 사유" style={inputStyle} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setCancelTarget(null)} disabled={actionBusy} style={btnStyle('var(--color-gray-500)')}>닫기</button>
              <button onClick={handleCancel} disabled={actionBusy} style={btnStyle('var(--color-red-600)')}>
                {actionBusy ? '처리 중...' : '취소 확정'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const fieldLabel: React.CSSProperties = {
  display: 'block', fontSize: '12px', color: 'var(--color-gray-600)', marginBottom: '4px',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)', fontSize: '13px',
};
function btnStyle(bg: string): React.CSSProperties {
  return {
    padding: '6px 12px', borderRadius: '6px', border: 'none', background: bg, color: 'white',
    fontSize: '12px', fontWeight: 500, cursor: 'pointer',
  };
}
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
};
const dialogStyle: React.CSSProperties = {
  background: 'white', borderRadius: '12px', padding: '20px', width: '420px', maxWidth: '92vw',
};
